/**
 * components/cardeditor/Inspector.jsx — right-side properties panel.
 *
 * Chunk 3 (Apr 24 2026).
 *
 * Shows controls for the currently selected element. Each element
 * type gets its own control set — text elements have font controls,
 * images have fit/corner radius, QR has error-correction etc.
 *
 * Uses the same updateElement(id, patch) API the canvas uses. All
 * mutations go through the editor hook so undo/redo covers them.
 *
 * If nothing selected → shows a hint. If deletable element selected
 * → shows a Delete button.
 */

import { canDelete, MIN_DIMENSIONS, QR_MIN_MM } from '../../lib/cards/layout-constraints.js';

export function Inspector({ editor }) {
  const { selectedElement, updateElement, deleteElement } = editor;

  if (!selectedElement) {
    return (
      <aside className="card-editor-inspector" aria-label="Inspector">
        <div className="inspector-empty muted" style={{ padding: 'var(--sp-4)', fontSize: 'var(--fs-sm)' }}>
          Click an element on the card to edit its properties.
        </div>
      </aside>
    );
  }

  const el = selectedElement;
  const setField = (field, value) => updateElement(el.id, { [field]: value });
  const setProp = (propKey, value) => updateElement(el.id, { props: { [propKey]: value } });

  return (
    <aside className="card-editor-inspector" aria-label="Inspector" style={{
      padding: 'var(--sp-4)',
      borderLeft: '1px solid var(--line-soft)',
      minWidth: 260,
      maxWidth: 320,
      overflowY: 'auto',
    }}>
      <h4 style={{ margin: 0, marginBottom: 'var(--sp-3)', fontSize: 'var(--fs-sm)' }}>
        {prettyTypeName(el.type)}
      </h4>
      <p className="muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 0 }}>
        {el.id}
      </p>

      {/* Position + size */}
      <Section title="Position & size">
        <Row>
          <NumberField label="X (mm)" value={el.x} onChange={(v) => setField('x', v)} step={0.5} />
          <NumberField label="Y (mm)" value={el.y} onChange={(v) => setField('y', v)} step={0.5} />
        </Row>
        <Row>
          <NumberField
            label="Width (mm)"
            value={el.w}
            onChange={(v) => setField('w', v)}
            step={0.5}
            min={(MIN_DIMENSIONS[el.type] || {}).w || 3}
          />
          <NumberField
            label="Height (mm)"
            value={el.h}
            onChange={(v) => setField('h', v)}
            step={0.5}
            min={(MIN_DIMENSIONS[el.type] || {}).h || 3}
          />
        </Row>
      </Section>

      {/* Text-specific */}
      {isTextType(el.type) && (
        <Section title="Text">
          <NumberField
            label="Font size (pt)"
            value={el.props?.fontSize || 10}
            onChange={(v) => setProp('fontSize', v)}
            step={0.5}
            min={4}
            max={48}
          />
          <SelectField
            label="Weight"
            value={el.props?.fontWeight || 'normal'}
            options={[{ value: 'normal', label: 'Normal' }, { value: 'bold', label: 'Bold' }]}
            onChange={(v) => setProp('fontWeight', v)}
          />
          <SelectField
            label="Align"
            value={el.props?.align || 'left'}
            options={[
              { value: 'left', label: 'Left' },
              { value: 'center', label: 'Center' },
              { value: 'right', label: 'Right' },
            ]}
            onChange={(v) => setProp('align', v)}
          />
          <ColorField
            label="Colour"
            value={resolveColorString(el.props?.color)}
            onChange={(v) => setProp('color', v)}
          />
          {el.type === 'static_text' && (
            <TextField
              label="Text"
              value={el.props?.text || ''}
              onChange={(v) => setProp('text', v)}
            />
          )}
          <CheckboxField
            label="UPPERCASE"
            checked={!!el.props?.uppercase}
            onChange={(v) => setProp('uppercase', v)}
          />
        </Section>
      )}

      {/* Image-specific */}
      {isImageType(el.type) && (
        <Section title="Image">
          <SelectField
            label="Fit"
            value={el.props?.fit || 'cover'}
            options={[
              { value: 'cover', label: 'Cover (fill)' },
              { value: 'contain', label: 'Contain (fit inside)' },
            ]}
            onChange={(v) => setProp('fit', v)}
          />
          {el.type === 'dynamic_photo' && (
            <NumberField
              label="Corner radius (mm)"
              value={el.props?.cornerRadius || 0}
              onChange={(v) => setProp('cornerRadius', v)}
              step={0.5}
              min={0}
              max={6}
            />
          )}
        </Section>
      )}

      {/* Rect-specific */}
      {el.type === 'static_rect' && (
        <Section title="Rectangle">
          <ColorField
            label="Fill"
            value={resolveColorString(el.props?.fill)}
            onChange={(v) => setProp('fill', v)}
          />
          <NumberField
            label="Corner radius (mm)"
            value={el.props?.cornerRadius || 0}
            onChange={(v) => setProp('cornerRadius', v)}
            step={0.5}
            min={0}
            max={10}
          />
        </Section>
      )}

      {/* QR-specific */}
      {el.type === 'dynamic_qr' && (
        <div className="alert" style={{
          background: 'var(--color-surface-alt, #f5f5f5)',
          padding: 'var(--sp-3)', fontSize: 'var(--fs-xs)',
          borderRadius: 6, margin: 'var(--sp-3) 0',
        }}>
          QR codes must be at least {QR_MIN_MM}mm square to scan reliably. The editor enforces this.
        </div>
      )}

      {/* Delete */}
      {canDelete(el) && (
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => deleteElement(el.id)}
          style={{
            marginTop: 'var(--sp-4)',
            color: '#b42318',
            width: '100%',
          }}
        >
          Delete element
        </button>
      )}

      {!canDelete(el) && (
        <p className="muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--sp-4)' }}>
          This element is required and can't be deleted.
        </p>
      )}
    </aside>
  );
}

/* ─── UI building blocks ─── */

function Section({ title, children }) {
  return (
    <section style={{ marginTop: 'var(--sp-4)' }}>
      <h5 style={{
        margin: 0, marginBottom: 'var(--sp-2)',
        fontSize: 'var(--fs-xs)',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        color: 'var(--text-muted, #666)',
      }}>{title}</h5>
      {children}
    </section>
  );
}

function Row({ children }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', marginBottom: 'var(--sp-2)' }}>
      {children}
    </div>
  );
}

function NumberField({ label, value, onChange, step = 1, min, max }) {
  return (
    <label style={{ display: 'block', fontSize: 'var(--fs-xs)', flex: 1 }}>
      <span style={{ display: 'block', color: 'var(--text-muted, #666)' }}>{label}</span>
      <input
        type="number"
        value={typeof value === 'number' ? value : 0}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (!Number.isFinite(v)) return;
          let clamped = v;
          if (min !== undefined && clamped < min) clamped = min;
          if (max !== undefined && clamped > max) clamped = max;
          onChange(clamped);
        }}
        step={step}
        min={min}
        max={max}
        className="field-input"
        style={{ width: '100%', padding: 'var(--sp-1) var(--sp-2)', fontSize: 'var(--fs-sm)' }}
      />
    </label>
  );
}

function TextField({ label, value, onChange }) {
  return (
    <label style={{ display: 'block', fontSize: 'var(--fs-xs)', marginBottom: 'var(--sp-2)' }}>
      <span style={{ display: 'block', color: 'var(--text-muted, #666)' }}>{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="field-input"
        style={{ width: '100%', padding: 'var(--sp-1) var(--sp-2)', fontSize: 'var(--fs-sm)' }}
      />
    </label>
  );
}

function SelectField({ label, value, options, onChange }) {
  return (
    <label style={{ display: 'block', fontSize: 'var(--fs-xs)', marginBottom: 'var(--sp-2)' }}>
      <span style={{ display: 'block', color: 'var(--text-muted, #666)' }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="field-input"
        style={{ width: '100%', padding: 'var(--sp-1) var(--sp-2)', fontSize: 'var(--fs-sm)' }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function ColorField({ label, value, onChange }) {
  // Accept 'brand' or 'auto-contrast' as special values alongside hex
  const isSpecial = value === 'brand' || value === 'auto-contrast';
  const hex = isSpecial ? '#000000' : (value || '#000000');

  return (
    <div style={{ marginBottom: 'var(--sp-2)' }}>
      <label style={{ display: 'block', fontSize: 'var(--fs-xs)' }}>
        <span style={{ display: 'block', color: 'var(--text-muted, #666)' }}>{label}</span>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
          <input
            type="color"
            value={hex}
            onChange={(e) => onChange(e.target.value)}
            disabled={isSpecial}
            style={{ width: 40, height: 32, padding: 0, border: '1px solid var(--line-soft)' }}
          />
          <select
            value={isSpecial ? value : 'custom'}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'custom') onChange(hex);
              else onChange(v);
            }}
            className="field-input"
            style={{ flex: 1, padding: 'var(--sp-1) var(--sp-2)', fontSize: 'var(--fs-sm)' }}
          >
            <option value="custom">Custom</option>
            <option value="brand">Brand colour</option>
            <option value="auto-contrast">Auto contrast</option>
          </select>
        </div>
      </label>
    </div>
  );
}

function CheckboxField({ label, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginBottom: 'var(--sp-2)', fontSize: 'var(--fs-xs)' }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

/* ─── helpers ─── */

function isTextType(type) {
  return [
    'static_text', 'dynamic_name', 'dynamic_role', 'dynamic_footer_text',
  ].includes(type);
}

function isImageType(type) {
  return [
    'dynamic_photo', 'dynamic_logo', 'dynamic_banner', 'static_image',
  ].includes(type);
}

function resolveColorString(value) {
  if (!value) return '#000000';
  if (value === 'brand' || value === 'auto-contrast') return value;
  if (typeof value === 'string' && value.startsWith('#')) return value;
  return '#000000';
}

function prettyTypeName(type) {
  const map = {
    dynamic_photo: 'Staff photo',
    dynamic_logo: 'Logo',
    dynamic_name: 'Staff name',
    dynamic_role: 'Staff role',
    dynamic_qr: 'QR code',
    dynamic_banner: 'Banner',
    dynamic_footer_text: 'Footer text',
    static_text: 'Text',
    static_rect: 'Rectangle',
    static_image: 'Image',
    static_divider: 'Divider',
  };
  return map[type] || type;
}
